/**
 * Izračuni treninga. Jedna definicija za portal, trenera i analitiku
 * (ARHITEKTURA.md, sekcija 18 — "ovo je jedina definicija u sustavu").
 */

export interface LoggedSet {
  setIndex: number;
  reps: number | null;
  weightKg: number | null;
  /** Popunjeno samo kod vjezbi tipa `timed` (plank). */
  durationSeconds?: number | null;
  done: boolean;
}

export interface ExerciseLog {
  exerciseId: string;
  exerciseName: string;
  sets: LoggedSet[];
}

export const SET_LIMITS = {
  repsMin: 0,
  repsMax: 1000,
  weightMin: 0,
  weightMax: 1000,
  durationMin: 0,
  /** Osam sati — gornja granica postoji zbog krivog unosa, ne zbog stvarnog seta. */
  durationMax: 28800,
} as const;

export type SetFieldError = 'reps' | 'weight' | 'duration' | null;

/** Ista pravila kao `setLogInput` u `@st/contract` — validacija ne živi na dva mjesta različito. */
export function validateSet(set: {
  reps: number | '';
  weightKg: number | '';
  durationSeconds?: number | '';
}): SetFieldError {
  const { reps, weightKg } = set;
  const duration = set.durationSeconds ?? '';
  if (reps !== '') {
    if (!Number.isInteger(reps) || reps < SET_LIMITS.repsMin || reps > SET_LIMITS.repsMax) {
      return 'reps';
    }
  }
  if (weightKg !== '') {
    if (
      !Number.isFinite(weightKg) ||
      weightKg < SET_LIMITS.weightMin ||
      weightKg > SET_LIMITS.weightMax
    ) {
      return 'weight';
    }
  }
  if (duration !== '') {
    if (
      !Number.isInteger(duration) ||
      duration < SET_LIMITS.durationMin ||
      duration > SET_LIMITS.durationMax
    ) {
      return 'duration';
    }
  }
  return null;
}

/** Volumen = Σ (ponavljanja × kilaža) po odrađenim setovima. Nedovršeni setovi se ne broje. */
export function setVolume(set: LoggedSet): number {
  if (!set.done) return 0;
  const reps = set.reps ?? 0;
  const weight = set.weightKg ?? 0;
  if (reps <= 0 || weight <= 0) return 0;
  return reps * weight;
}

export function totalVolumeKg(exercises: readonly ExerciseLog[]): number {
  return exercises.reduce(
    (sum, ex) => sum + ex.sets.reduce((s, set) => s + setVolume(set), 0),
    0,
  );
}

export function completedSetCount(exercises: readonly ExerciseLog[]): number {
  return exercises.reduce((n, ex) => n + ex.sets.filter((s) => s.done).length, 0);
}

export type PrMetric = 'weight' | 'reps' | 'volume';

export interface PersonalRecord {
  exerciseId: string;
  exerciseName: string;
  metric: PrMetric;
  value: number;
  previous: number;
}

/** Prethodni rekordi po vježbi i metrici; sve što ih nadmaši je novi rekord. */
export type PreviousRecords = Record<string, Partial<Record<PrMetric, number>>>;

export function findPersonalRecords(
  exercises: readonly ExerciseLog[],
  previous: PreviousRecords,
): PersonalRecord[] {
  const out: PersonalRecord[] = [];
  for (const ex of exercises) {
    const done = ex.sets.filter((s) => s.done);
    if (done.length === 0) continue;

    const best: Record<PrMetric, number> = {
      weight: Math.max(0, ...done.map((s) => s.weightKg ?? 0)),
      reps: Math.max(0, ...done.map((s) => s.reps ?? 0)),
      volume: Math.max(0, ...done.map(setVolume)),
    };

    for (const metric of ['weight', 'reps', 'volume'] as const) {
      const prev = previous[ex.exerciseId]?.[metric] ?? 0;
      if (best[metric] > prev && best[metric] > 0) {
        out.push({
          exerciseId: ex.exerciseId,
          exerciseName: ex.exerciseName,
          metric,
          value: best[metric],
          previous: prev,
        });
      }
    }
  }
  return out;
}

/** Dosljednost = odrađeni / propisani treninzi u razdoblju (ARHITEKTURA.md sekcija 18). */
export function consistency(done: number, planned: number): number {
  if (planned <= 0) return 0;
  return Math.min(1, done / planned);
}

const MISSED_GRACE_MS = 48 * 60 * 60 * 1000;

/**
 * Trening je propušten ako je njegov datum prošao za više od 48 sati,
 * a nema zapisa sa `completedAt`.
 */
export function isMissed(
  scheduledFor: Date | string,
  completedAt: Date | string | null,
  now: Date = new Date(),
): boolean {
  if (completedAt) return false;
  const scheduled = typeof scheduledFor === 'string' ? new Date(scheduledFor) : scheduledFor;
  return now.getTime() - scheduled.getTime() > MISSED_GRACE_MS;
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
